import React, { useEffect, useState, useRef } from "react";
// Make sure this path is correct
import { getActivitiesByProjectId } from "../api/activityApi";
import { FiEye, FiX, FiActivity, FiCalendar, FiAlertOctagon, FiFlag, FiFolder, FiCheckCircle, FiClock, FiTarget } from "react-icons/fi";
import AddTaskModal from "../pages/programManagerDashboard/AddTaskModal";

const ActivityTable = ({ projectId }) => {
  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // State for the Add Task modal
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [activityToAddTask, setActivityToAddTask] = useState(null);
  
  const modalRef = useRef(null);

  const fetchActivities = async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedActivities = await getActivitiesByProjectId(projectId);
      setActivities(fetchedActivities);
    } catch (err) {
      console.error("Error fetching activities:", err.message);
      setError(err.message || "Failed to fetch activities.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchActivities();
    }
  }, [projectId]);

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) && selectedActivity) {
        handleCloseView();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedActivity]);

  const calculateProgress = (activity) => {
    const total = Array.isArray(activity.tasks) ? activity.tasks.length : 0;
    const completed = Array.isArray(activity.tasks)
      ? activity.tasks.filter((task) => task.status === "Completed").length
      : 0;
    return total === 0 ? 0 : Math.round((completed / total) * 100);
  };

  //Status styling for better visual feedback
  const getStatusStyle = (status) => {
    switch (status) {
      case "In Progress":
        return "text-blue-600 bg-blue-100 px-3 py-1 rounded-full text-xs font-medium";
      case "Pending":
        return "text-yellow-600 bg-yellow-100 px-3 py-1 rounded-full text-xs font-medium";
      case "Completed":
        return "text-green-600 bg-green-100 px-3 py-1 rounded-full text-xs font-medium";
      default:
        return "text-gray-600 bg-gray-100 px-3 py-1 rounded-full text-xs font-medium";
    }
  };

  // Close the detailed activity view
  const handleCloseView = () => {
    setSelectedActivity(null);
  };

  // Open the Add Task modal for a specific activity
  const handleOpenAddTaskModal = (activity) => {
    setActivityToAddTask(activity);
    setIsAddTaskModalOpen(true);
  };

  // Close the Add Task modal and refresh the activity list
  const handleTaskCreated = () => {
    setIsAddTaskModalOpen(false);
    setActivityToAddTask(null);
    fetchActivities(); // Refresh activities to show the new task
  };

  if (loading) {
    return <div className="mt-6 text-center text-gray-500">Loading activities...</div>;
  }

  if (error) {
    if (error.includes("No activities found")) {
      return (
        <div className="mt-6 text-center text-gray-500">
          No activities for this project yet.
        </div>
      );
    }
    return <div className="mt-6 text-center text-red-500">Error: {error}</div>;
  }

  return (
    <div className="mt-6 bg-white rounded-xl shadow-md overflow-hidden">
      <div className="p-4 sm:p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Activities</h2>
        {activities.length === 0 ? (
          <div className="text-center py-8">
            <div className="flex justify-center mb-3">
              <div className="p-3 bg-orange-100 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
            <p className="text-gray-500">No activities for this project yet.</p>
            <p className="text-sm text-gray-400 mt-1">Add activities to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-orange-50 text-gray-600 text-left">
                  <th className="px-4 py-3 text-orange-600 font-semibold">Title</th>
                  <th className="px-4 py-3 text-orange-600 font-semibold">Due Date</th>
                  <th className="px-4 py-3 text-orange-600 font-semibold">Priority</th>
                  <th className="px-4 py-3 text-orange-600 font-semibold">Status</th>
                  <th className="px-4 py-3 text-orange-600 font-semibold text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activities.map((activity, i) => (
                  <tr key={activity._id || i} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 font-medium">{activity.activityTitle}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <FiCalendar className="h-4 w-4 mr-2 text-gray-400" />
                        {new Date(activity.dueDate).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <FiAlertOctagon className="h-4 w-4 mr-2 text-gray-400" />
                        {activity.priority}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={getStatusStyle(activity.status)}>{activity.status}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => setSelectedActivity(activity)}
                          className="text-orange-600 hover:text-orange-800 p-1.5 rounded-full transition-colors"
                          title="View Details"
                        >
                          <FiEye size={18} />
                        </button>
                        <button
                          onClick={() => handleOpenAddTaskModal(activity)}
                          className="text-xs px-3 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                        >
                          Add Tasks
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Activity Details Modal */}
      {selectedActivity && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
          <div
            ref={modalRef}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative transform transition-all duration-300 ease-out"
          >
            {/* Header with Orange Theme */}
            <div className="bg-gradient-to-r from-orange-300 to-orange-400 rounded-t-2xl p-4 sm:p-6 border-b border-orange-500">
              <div className="flex justify-between items-center">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <FiActivity className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-black">
                      Activity Details
                    </h2>
                    <div className="flex items-center mt-1 text-black text-sm">
                      <FiFolder className="w-3 h-3 mr-1" />
                      <span>Programs Department</span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={handleCloseView} 
                  className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">{selectedActivity.activityTitle}</h3>
              
              <div className="space-y-4">
                {/* Description */}
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-1">Description</label>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                    {selectedActivity.description || "No description provided"}
                  </p>
                </div>
                
                {/* Date and Priority Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 mb-1">Due Date</label>
                    <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                      <FiCalendar className="mr-2 text-orange-500" />
                      <span className="text-gray-700">
                        {new Date(selectedActivity.dueDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500 mb-1">Priority</label>
                    <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                      <FiAlertOctagon className="mr-2 text-orange-500" />
                      <span className="text-gray-700">{selectedActivity.priority}</span>
                    </div>
                  </div>
                </div>
                
                {/* Status */}
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-1">Status</label>
                  <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <FiFlag className="mr-2 text-orange-500" />
                    <span className={getStatusStyle(selectedActivity.status)}>
                      {selectedActivity.status}
                    </span>
                  </div>
                </div>
                
                {/* Progress Section */}
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-1">Progress</label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Completion</span>
                      <span className="text-sm font-bold text-orange-600">
                        {calculateProgress(selectedActivity)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-orange-400 to-orange-500 h-3 rounded-full"
                        style={{ width: `${calculateProgress(selectedActivity)}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-gray-500">
                      <span>
                        {Array.isArray(selectedActivity.tasks) ? selectedActivity.tasks.filter(task => task.status === "Completed").length : 0} of {Array.isArray(selectedActivity.tasks) ? selectedActivity.tasks.length : 0} tasks completed
                      </span>
                      <span>{calculateProgress(selectedActivity)}%</span>
                    </div>
                  </div>
                </div>

                {/* Tasks Summary */}
                {Array.isArray(selectedActivity.tasks) && selectedActivity.tasks.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 mb-1">Tasks Summary</label>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                      {selectedActivity.tasks.slice(0, 3).map((task, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center">
                            <FiTarget className="mr-2 text-gray-400" />
                            <span className="text-sm text-gray-700 truncate max-w-[150px]">
                              {task.title}
                            </span>
                          </div>
                          <div className="flex items-center">
                            {task.status === "Completed" ? (
                              <FiCheckCircle className="text-green-500" />
                            ) : (
                              <FiClock className="text-yellow-500" />
                            )}
                          </div>
                        </div>
                      ))}
                      {selectedActivity.tasks.length > 3 && (
                        <div className="text-center">
                          <span className="text-xs text-gray-500">
                            +{selectedActivity.tasks.length - 3} more tasks
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Action Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => handleOpenAddTaskModal(selectedActivity)}
                  className="px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200"
                >
                  Add Task
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={isAddTaskModalOpen}
        onClose={() => setIsAddTaskModalOpen(false)}
        activityId={activityToAddTask?._id}
        onTaskCreated={handleTaskCreated}
      />
    </div>
  );
};

export default ActivityTable;